import type { InvestmentCompany } from '../types';
import { computeCompanyMetrics } from './calculations';
import { getInvestmentStatus } from './investmentCompanyUi';

export type ReturnScenario = 'medium' | 'good' | 'amazing';

export const SCENARIO_MULTIPLIERS: Record<ReturnScenario, number> = {
  medium: 2.5,
  good: 10,
  amazing: 20,
};

export const SCENARIO_LABELS: Record<ReturnScenario, string> = {
  medium: 'Medium (2.5×)',
  good: 'Good (10×)',
  amazing: 'Amazing (20×)',
};

export type FutureReturnProjection = {
  companyId: string;
  companyName: string;
  ticketSize: number;
  currentEquity: number;
  expectedLeadTime: number | null;
  expectedMultiple: number | null;
  expectedReturnDate: Date | null;
  scenarios: {
    medium: number;
    good: number;
    amazing: number;
    custom: number | null;
  };
};

export type TimelinePoint = {
  date: Date;
  companyId: string;
  companyName: string;
  scenario: ReturnScenario | 'custom';
  returnAmount: number;
  multiple: number;
};

export type AggregatedTimelinePoint = {
  year: number;
  medium: number;
  good: number;
  amazing: number;
  custom: number;
  companies: string[];
};

export function computeFutureReturnProjection(company: InvestmentCompany): FutureReturnProjection {
  const metrics = computeCompanyMetrics(company);
  const status = getInvestmentStatus(company);

  const ticketSize = company.ticketSize || 0;
  const currentEquity = status === 'dead' ? 0 : metrics.myEquityValue;

  const expectedLeadTime =
    company.expectedReturnLeadTime != null && Number.isFinite(company.expectedReturnLeadTime)
      ? company.expectedReturnLeadTime
      : null;

  const expectedMultiple =
    company.expectedReturnMultiple != null && Number.isFinite(company.expectedReturnMultiple)
      ? company.expectedReturnMultiple
      : null;

  const expectedReturnDate =
    expectedLeadTime != null
      ? new Date(Date.now() + expectedLeadTime * 365.25 * 24 * 60 * 60 * 1000)
      : null;

  return {
    companyId: company.id,
    companyName: company.name,
    ticketSize,
    currentEquity,
    expectedLeadTime,
    expectedMultiple,
    expectedReturnDate,
    scenarios: {
      medium: ticketSize * SCENARIO_MULTIPLIERS.medium,
      good: ticketSize * SCENARIO_MULTIPLIERS.good,
      amazing: ticketSize * SCENARIO_MULTIPLIERS.amazing,
      custom: expectedMultiple != null ? ticketSize * expectedMultiple : null,
    },
  };
}

export function computeAllFutureReturnProjections(
  companies: InvestmentCompany[]
): FutureReturnProjection[] {
  return companies
    .filter((c) => getInvestmentStatus(c) === 'active')
    .map((c) => computeFutureReturnProjection(c));
}

export function buildFutureReturnsTimeline(projections: FutureReturnProjection[]): TimelinePoint[] {
  const points: TimelinePoint[] = [];

  for (const proj of projections) {
    if (!proj.expectedReturnDate || proj.expectedLeadTime == null) continue;

    const scenarios: Array<{ scenario: ReturnScenario | 'custom'; multiple: number }> = [
      { scenario: 'medium', multiple: SCENARIO_MULTIPLIERS.medium },
      { scenario: 'good', multiple: SCENARIO_MULTIPLIERS.good },
      { scenario: 'amazing', multiple: SCENARIO_MULTIPLIERS.amazing },
    ];

    if (proj.expectedMultiple != null) {
      scenarios.push({ scenario: 'custom', multiple: proj.expectedMultiple });
    }

    for (const { scenario, multiple } of scenarios) {
      points.push({
        date: proj.expectedReturnDate,
        companyId: proj.companyId,
        companyName: proj.companyName,
        scenario,
        returnAmount: proj.ticketSize * multiple,
        multiple,
      });
    }
  }

  return points.sort((a, b) => a.date.getTime() - b.date.getTime());
}

export function aggregateTimelineByYear(
  projections: FutureReturnProjection[],
  maxYears = 15
): AggregatedTimelinePoint[] {
  const currentYear = new Date().getFullYear();
  const yearMap = new Map<number, AggregatedTimelinePoint>();

  for (let i = 0; i <= maxYears; i++) {
    const year = currentYear + i;
    yearMap.set(year, {
      year,
      medium: 0,
      good: 0,
      amazing: 0,
      custom: 0,
      companies: [],
    });
  }

  for (const proj of projections) {
    if (proj.expectedReturnDate == null) continue;

    const returnYear = proj.expectedReturnDate.getFullYear();
    const point = yearMap.get(returnYear);
    if (!point) continue;

    point.medium += proj.scenarios.medium;
    point.good += proj.scenarios.good;
    point.amazing += proj.scenarios.amazing;
    if (proj.scenarios.custom != null) {
      point.custom += proj.scenarios.custom;
    }
    if (!point.companies.includes(proj.companyName)) {
      point.companies.push(proj.companyName);
    }
  }

  return Array.from(yearMap.values())
    .filter((p) => p.medium > 0 || p.good > 0 || p.amazing > 0 || p.custom > 0)
    .sort((a, b) => a.year - b.year);
}

export function computeFutureReturnsTotals(projections: FutureReturnProjection[]): {
  totalTickets: number;
  totalCurrentEquity: number;
  medium: number;
  good: number;
  amazing: number;
  custom: number;
  projectionsWithTimeline: number;
} {
  let totalTickets = 0;
  let totalCurrentEquity = 0;
  let medium = 0;
  let good = 0;
  let amazing = 0;
  let custom = 0;
  let projectionsWithTimeline = 0;

  for (const proj of projections) {
    totalTickets += proj.ticketSize;
    totalCurrentEquity += proj.currentEquity;
    medium += proj.scenarios.medium;
    good += proj.scenarios.good;
    amazing += proj.scenarios.amazing;
    if (proj.scenarios.custom != null) {
      custom += proj.scenarios.custom;
    }
    if (proj.expectedLeadTime != null) {
      projectionsWithTimeline++;
    }
  }

  return {
    totalTickets,
    totalCurrentEquity,
    medium,
    good,
    amazing,
    custom,
    projectionsWithTimeline,
  };
}
